import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Mail, ArrowLeft, Loader2, CheckCircle2 } from 'lucide-react';
import { AuthShell } from './Register';

/**
 * ForgotPassword — enter email → we send a reset link if the address
 * has a password account. Never enumerates: same success screen for
 * "email not found" and "email found".
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not send reset link.');
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <AuthShell>
        <div className="text-center" data-testid="forgot-sent">
          <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto mb-4 flex items-center justify-center">
            <CheckCircle2 size={32} strokeWidth={2.5} className="text-emerald-600"/>
          </div>
          <h1 className="text-2xl font-extrabold text-[#2B2140] mb-2">Check your inbox</h1>
          <p className="text-sm text-slate-600 mb-6">
            If <span className="font-bold">{email}</span> has a password account, we've sent a reset link.
            The link expires in 1 hour.
          </p>
          <Link to="/login" className="text-sm font-bold text-[#5B8DEF] inline-flex items-center gap-1">
            <ArrowLeft size={14}/> Back to sign in
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <Link to="/login" data-testid="forgot-back-btn"
        className="text-sm font-bold text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={14}/> Back
      </Link>
      <h1 className="text-3xl font-extrabold text-[#2B2140] mb-1">Forgot password?</h1>
      <p className="text-sm text-slate-500 mb-6">Enter your email — we'll send a link to reset it.</p>
      <form onSubmit={onSubmit} className="space-y-3">
        <label className="flex items-center gap-3 px-4 rounded-2xl border-2 border-slate-200 bg-white focus-within:border-[#FF6B1A]">
          <Mail size={18} className="text-slate-400"/>
          <input data-testid="forgot-email" type="email" required placeholder="Email address"
            value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email"
            className="flex-1 bg-transparent outline-none py-3 text-[#2B2140]"/>
        </label>
        {error && <p data-testid="forgot-error" className="text-sm text-rose-600">{error}</p>}
        <button data-testid="forgot-submit-btn" type="submit" disabled={busy}
          className="pushable w-full bg-[#FF6B1A] text-white text-lg font-bold py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {busy && <Loader2 size={16} className="animate-spin"/>}
          Send reset link
        </button>
      </form>
    </AuthShell>
  );
}
